/**
 * Outreach API — scrape chat members into a pool, run DM campaigns.
 *
 * Separate tool from the chat autoposter. Mass DMs risk account bans, so the
 * UI surfaces warnings and the backend hard-throttles.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export type CampaignStatus = "draft" | "running" | "paused";
export type ScrapeMethod = "messages" | "participants";

export interface ScrapeResponse {
  scraped: number;
  skipped: number;
  pool_size: number;
}

export interface ManualUserImportResponse {
  imported: number;
  skipped: number;
  pool_size: number;
}

export interface ScrapedUser {
  id: number;
  user_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  is_premium: boolean;
  source_chat_title: string | null;
  created_at: string;
}

export interface PoolResponse {
  total: number;
  contacted: number;
  limit: number;
  offset: number;
  has_more: boolean;
  items: ScrapedUser[];
}

export interface Campaign {
  id: number;
  name: string;
  template_id: number | null;
  template_name: string | null;
  account_ids: number[];
  status: CampaignStatus;
  min_interval_seconds: number;
  max_interval_seconds: number;
  daily_limit_per_account: number;
  sent_count: number;
  created_at: string;
}

export interface CampaignCreate {
  name?: string;
  template_id?: number | null;
  account_ids?: number[];
  min_interval_seconds?: number;
  max_interval_seconds?: number;
  daily_limit_per_account?: number;
}

export interface CampaignPatch {
  name?: string;
  template_id?: number | null;
  account_ids?: number[];
  min_interval_seconds?: number;
  max_interval_seconds?: number;
  daily_limit_per_account?: number;
}

// ============================================================
// Queries
// ============================================================

export function usePool(opts?: { refetchInterval?: number; limit?: number; offset?: number }) {
  const limit = opts?.limit ?? 20;
  const offset = opts?.offset ?? 0;
  return useQuery<PoolResponse, ApiError>({
    queryKey: ["outreach", "pool", limit, offset],
    queryFn: () => api.get<PoolResponse>(`/outreach/pool?limit=${limit}&offset=${offset}`),
    refetchInterval: opts?.refetchInterval,
    staleTime: 5_000,
  });
}

export function useCampaigns(opts?: { refetchInterval?: number }) {
  return useQuery<Campaign[], ApiError>({
    queryKey: ["outreach", "campaigns"],
    queryFn: () => api.get<Campaign[]>("/outreach/campaigns"),
    // Poll while a campaign runs so sent_count updates live.
    refetchInterval: opts?.refetchInterval ?? 6_000,
    staleTime: 3_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useScrapeChat() {
  const qc = useQueryClient();
  return useMutation<
    ScrapeResponse,
    ApiError,
    { chat_id: number; account_id?: number | null; method?: ScrapeMethod; message_limit?: number }
  >({
    mutationFn: (body) => api.post<ScrapeResponse>("/outreach/scrape", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "pool"] }),
  });
}

export function useImportUsers() {
  const qc = useQueryClient();
  return useMutation<ManualUserImportResponse, ApiError, { text: string; account_id?: number | null }>({
    mutationFn: (body) => api.post<ManualUserImportResponse>("/outreach/import-users", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "pool"] }),
  });
}

export function useDeletePoolUser() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api.delete<void>(`/outreach/pool/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "pool"] }),
  });
}

export function useClearPool() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () => api.delete<void>("/outreach/pool"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "pool"] }),
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiError, CampaignCreate>({
    mutationFn: (body) => api.post<Campaign>("/outreach/campaigns", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "campaigns"] }),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiError, { id: number; patch: CampaignPatch }>({
    mutationFn: ({ id, patch }) => api.patch<Campaign>(`/outreach/campaigns/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "campaigns"] }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api.delete<void>(`/outreach/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "campaigns"] }),
  });
}

export function useStartCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiError, number>({
    mutationFn: (id) => api.post<Campaign>(`/outreach/campaigns/${id}/start`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "campaigns"] }),
  });
}

export function useStopCampaign() {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiError, number>({
    mutationFn: (id) => api.post<Campaign>(`/outreach/campaigns/${id}/stop`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach", "campaigns"] }),
  });
}
